// War status at a date, active wars, and UCDP interstate dyads.
//
// A War's `status` claim is dated at curation time (near the as-of date), so for earlier dates:
//
//   date < from                          → null (the war did not exist)
//   date ≥ status.as_of                  → the curated claim itself
//   from ≤ date < to (closed span)       → 'active', dated by the newest evidence ≤ date
//   open span, curated status 'active'   → 'active', same dating
//   open span, curated status not active → 'ongoing' (confidence low): the war existed but the
//                                          data does not say when it froze or paused
//   date ≥ to, before status.as_of       → 'ended' (or the curated non-active value), as of `to`

import type { ActorId, Claim, DateStr, SourceId, Statement, War } from '../core/schema.ts';
import { inSpan, yearOf } from '../core/dates.ts';
import type { UcdpData } from '../data/types.ts';

export const ACTIVE = 'active';
export const ONGOING = 'ongoing';
const ENDED = 'ended';
const INTERSTATE = 'interstate';

/** Newest dated fact about the war at or before `date`: its start or a key development. */
function evidenceAt(war: War, date: DateStr): DateStr {
  let best = war.from;
  for (const e of war.events) {
    if (e.date <= date && e.date > best) {
      best = e.date;
    }
  }
  return best;
}

function claim(value: string, as_of: DateStr, sources: SourceId[], confidence: Claim<string>['confidence']): Claim<string> {
  return { value, as_of, sources, confidence };
}

export function warStatus(war: War, date: DateStr): Claim<string> | null {
  if (date < war.from) {
    return null;
  }
  const s = war.status;
  if (date >= s.as_of) {
    return s;
  }

  if (war.to !== null && date >= war.to) {
    const value = s.value === ACTIVE ? ENDED : s.value;
    return claim(value, war.to, war.sources, s.confidence);
  }
  if (war.to !== null || s.value === ACTIVE) {
    return claim(ACTIVE, evidenceAt(war, date), war.sources, s.confidence);
  }
  return claim(ONGOING, evidenceAt(war, date), war.sources, 'low');
}

// ─── parties ─────────────────────────────────────────────────────────────────────────────

/** When a party took part: its span, a null bound being the war's own. `to` is exclusive. */
export function partyWindow(war: War, id: ActorId): { from: DateStr; to: DateStr | null } {
  const s = war.party_spans?.find((p) => p.actor === id);
  return { from: s?.from ?? war.from, to: s?.to ?? war.to };
}

/** True when `id` took part in the war on `date` (a party with no span took part for the whole war). */
export function partyAt(war: War, id: ActorId, date: DateStr): boolean {
  return inSpan(partyWindow(war, id), date);
}

/** The sides of the war on `date`: parties that joined later or left earlier are left out. */
export function sidesAt(war: War, date: DateStr): War['sides'] {
  if (!war.party_spans) {
    return war.sides;
  }
  return {
    a: war.sides.a.filter((id) => partyAt(war, id, date)),
    b: war.sides.b.filter((id) => partyAt(war, id, date)),
  };
}

/** The war with the sides of `date`; the war itself when it has no party spans. */
export function warAt(war: War, date: DateStr): War {
  return war.party_spans ? { ...war, sides: sidesAt(war, date) } : war;
}

/** The earlier of two end dates; null is no end. */
function earlierEnd(a: DateStr | null, b: DateStr | null): DateStr | null {
  if (a === null || b === null) {
    return a ?? b;
  }
  return a < b ? a : b;
}

/** The days both states were parties together, or null when they never were. */
export function pairWindow(war: War, a: ActorId, b: ActorId): { from: DateStr; to: DateStr | null } | null {
  const x = partyWindow(war, a);
  const y = partyWindow(war, b);
  const from = x.from > y.from ? x.from : y.from;
  const to = earlierEnd(x.to, y.to);
  if (to !== null && to <= from) {
    return null;
  }
  return { from, to };
}

/** One war as it stood on a date: nothing dated after it is in the view. */
export interface WarView {
  war: War;
  status: Claim<string>;
  /** Null when the curated claim is dated after `date` (it says nothing about then). */
  intensity: Claim<string> | null;
  /** Key developments dated ≤ date, newest first. */
  events: War['events'];
  /** Statements on this war by any party (states and non-state actors) dated ≤ date, newest first. */
  statements: Statement[];
  /** The end date, once it has passed. */
  ended: DateStr | null;
}

function newestFirst(a: { date: DateStr }, b: { date: DateStr }): number {
  return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
}

/** The war at `date`, or null when it had not begun. */
export function warViewAt(war: War, statements: Statement[], date: DateStr): WarView | null {
  const status = warStatus(war, date);
  if (!status) {
    return null;
  }
  return {
    war: warAt(war, date),
    status,
    intensity: war.intensity.as_of <= date ? war.intensity : null,
    events: war.events.filter((e) => e.date <= date).sort(newestFirst),
    statements: statements.filter((s) => s.war === war.id && s.date <= date).sort(newestFirst),
    ended: war.to !== null && war.to <= date ? war.to : null,
  };
}

/** Fighting (or of unknown phase inside its span) at `date`. */
export function isLive(war: War, date: DateStr): boolean {
  const v = warStatus(war, date)?.value;
  return v === ACTIVE || v === ONGOING;
}

/** Known to be in active hostilities at `date` (what the 'war' relation needs). */
export function isActive(war: War, date: DateStr): boolean {
  return warStatus(war, date)?.value === ACTIVE;
}

/** Wars fighting at `date`, each with the sides of that date. */
export function liveWars(wars: War[], date: DateStr): War[] {
  return wars.filter((w) => isLive(w, date)).map((w) => warAt(w, date));
}

// ─── UCDP ────────────────────────────────────────────────────────────────────────────────

export interface Dyad {
  a: string[];
  b: string[];
  name: string;
  year: number;
  /** Episode bounds when UCDP gives them, to trim the year. */
  start: DateStr | null;
  end: DateStr | null;
  sources: SourceId[];
}

/** Interstate conflict-years by calendar year. */
export type DyadIndex = Map<number, Dyad[]>;

export function dyadIndex(ucdp: UcdpData): DyadIndex {
  const out: DyadIndex = new Map();
  for (const c of ucdp.conflicts) {
    for (const y of c.years) {
      if (y.type !== INTERSTATE || y.a.length === 0 || y.b.length === 0) {
        continue;
      }
      const d: Dyad = {
        a: y.a, b: y.b, name: c.name ?? `UCDP conflict ${c.id}`, year: y.y, start: y.ep_start, end: y.ep_end,
        sources: c.sources,
      };
      const list = out.get(y.y) ?? [];
      list.push(d);
      out.set(y.y, list);
    }
  }
  return out;
}

/** Interstate dyads whose conflict-year covers `date` (trimmed by episode start/end). */
export function dyadsAt(ix: DyadIndex, date: DateStr): Dyad[] {
  return (ix.get(yearOf(date)) ?? []).filter((d) => {
    if (d.start !== null && date < d.start) {
      return false;
    }
    return d.end === null || date < d.end;
  });
}
